import React from 'react';

import { Stack, Typography } from '@mui/material';
import { makeStyles } from 'tss-react/mui';

export interface DialogHeaderProps {
  readonly title: string;
  /** Framed in a tile to the left of the title; logos and icons are sized to fit it. */
  readonly icon?: React.ReactNode;
  readonly subtitle?: string;
}

const useStyles = makeStyles()(theme => ({
  /** The margin `AlineDialog` gives a string title, so the header lines up with the dialog's content. */
  header: {
    margin: theme.spacing(1, 1, 0),
  },
  icon: {
    '& img, & svg': { height: 28, width: 28 },
    alignItems: 'center',
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: '12px',
    display: 'flex',
    flexShrink: 0,
    height: 48,
    justifyContent: 'center',
    width: 48,
  },
}));

/** A dialog title with an optional icon tile beside it and a subtitle under it. */
export const DialogHeader = React.memo(function DialogHeader({
  icon,
  subtitle,
  title,
}: DialogHeaderProps): JSX.Element {
  const { classes } = useStyles();

  return (
    <Stack className={classes.header} direction="row" sx={{ alignItems: 'center', gap: 2 }}>
      {Boolean(icon) && <div className={classes.icon}>{icon}</div>}
      <Stack sx={{ gap: 0.5 }}>
        <Typography variant="h1">{title}</Typography>
        {Boolean(subtitle) && (
          <Typography color="text.secondary" sx={{ fontSize: 14 }}>
            {subtitle}
          </Typography>
        )}
      </Stack>
    </Stack>
  );
});
